import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function JobManagementPage() {
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertMsg, setAlertMsg] = useState(null);
    const location = useLocation();
    

    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    
    // Xử lý thông báo (Toast)
    useEffect(() => {
        if (location.state?.successMessage) {
            setAlertMsg(location.state.successMessage);
            const timer = setTimeout(() => setAlertMsg(null), 3000);
            window.history.replaceState({}, document.title);
            return () => clearTimeout(timer);
        }
    }, [location.state]);

    // Fetch dữ liệu thật
    useEffect(() => {
        const fetchMyJobs = async () => {
            try {
                const token = localStorage.getItem('skillmatch_token');
                const res = await fetch(`${API_URL}/api/jobs/my-jobs`, {
                    headers: { 'x-auth-token': token }
                });
                if (res.ok) {
                    const data = await res.json();
                    setJobs(data);
                }
            } catch (error) { console.error(error); } finally { setLoading(false); }
        };
        fetchMyJobs();
    }, [API_URL]);

    // Hàm format ngày an toàn
    const formatDate = (dateString, isEndDate = false) => {
        if (!dateString) return isEndDate ? 'Không giới hạn' : '---';
        
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '---';

        return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // --- LOGIC TRẠNG THÁI NÂNG CAO ---
    const getDisplayStatus = (job) => {
        const now = new Date();
        const start = job.startDate ? new Date(job.startDate) : null;
        // ✅ SỬA: Dùng job.endDate thay vì job.deadline
        const end = job.endDate ? new Date(job.endDate) : null;

        // 1. Nếu là nháp hoặc tạm dừng do người dùng set -> Ưu tiên hiển thị
        if (job.status === 'DRAFT') {
            return { label: 'Bản nháp', className: 'bg-gray-100 text-gray-600 border-gray-200' };
        }
        if (job.status === 'Tạm dừng') {
             return { label: 'Tạm dừng', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
        }

        // 2. Kiểm tra ngày tháng để ghi đè trạng thái hiển thị
        if (end && now > end) {
            return { label: 'Đã kết thúc', className: 'bg-red-50 text-red-600 border-red-200' };
        }
        
        if (start && now < start) {
            return { label: 'Sắp mở', className: 'bg-blue-50 text-blue-600 border-blue-200' };
        }

        // 3. Mặc định
        return { label: 'Đang tuyển', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    };

    return (
        <div className="space-y-6">
            {alertMsg && (
                <div className="fixed top-20 right-5 z-50 bg-emerald-50 border border-emerald-200 text-emerald-700 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-fade-in-up">
                    <span className="material-symbols-outlined text-emerald-600">check_circle</span>
                    <span className="font-medium">{alertMsg}</span>
                </div>
            )}

            {/* HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Quản lý tuyển dụng</h1>
                    <p className="text-gray-500 text-sm mt-1">Theo dõi hiệu quả và quản lý các vị trí đang mở.</p>
                </div>
                <button
                    onClick={() => navigate('/business/jobs/new')}
                    className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md active:transform active:scale-95"
                >
                    <span className="material-symbols-outlined">add</span> Đăng tin mới
                </button>
            </div>

            {/* LIST JOBS */}
            {loading ? (
                 <div className="flex h-64 items-center justify-center text-gray-400">Đang tải dữ liệu...</div>
            ) : jobs.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-16 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="material-symbols-outlined text-3xl text-gray-400">work_outline</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Chưa có tin tuyển dụng</h3>
                    <p className="text-gray-500 mt-2 mb-6 max-w-sm mx-auto">Hãy tạo tin tuyển dụng đầu tiên để tiếp cận hàng nghìn ứng viên tiềm năng.</p>
                    <button onClick={() => navigate('/business/jobs/new')} className="text-blue-600 font-bold hover:underline">Tạo tin ngay</button>
                </div>
            ) : (
                <div className="grid gap-4">
                    {jobs.map(job => {
                        const statusObj = getDisplayStatus(job);
                        return (
                            <div key={job._id} onClick={() => navigate(`/business/jobs/edit/${job._id}`)} 
                                className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col md:flex-row gap-6 hover:shadow-lg transition-all cursor-pointer group relative overflow-hidden">
                                
                                {/* Decorative bar */}
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-blue-600 transition-colors"></div>

                                <div className="w-14 h-14 rounded-xl bg-gray-50 text-gray-600 flex items-center justify-center shrink-0 border border-gray-100 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                    <span className="material-symbols-outlined text-2xl">work</span>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate pr-4">
                                                {job.title}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusObj.className}`}>
                                                    {statusObj.label}
                                                </span>
                                                <span className="hidden sm:inline text-gray-300">•</span>
                                                <span>{job.type}</span>
                                                <span className="hidden sm:inline text-gray-300">•</span>
                                                <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">location_on</span> {job.location}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
                                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                                            <span className="text-gray-500">Lương:</span>
                                            <span className="font-semibold text-gray-900">{job.salary}</span>
                                        </div>
                                        
                                        {/* ✅ HIỂN THỊ NGÀY ĐĂNG & HẠN NỘP */}
                                        <div className="flex items-center gap-4 text-gray-500">
                                            <div className="flex items-center gap-1.5" title="Ngày bắt đầu">
                                                <span className="material-symbols-outlined text-[18px]">calendar_today</span>
                                                <span>{formatDate(job.startDate || job.createdAt)}</span>
                                            </div>
                                            <span className="text-gray-300">|</span>
                                            <div className="flex items-center gap-1.5 text-orange-600 font-medium" title="Hạn nộp hồ sơ">
                                                <span className="material-symbols-outlined text-[18px]">event_busy</span>
                                                {/* ✅ SỬA: Dùng job.endDate */}
                                                <span>{formatDate(job.endDate, true)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-row md:flex-col justify-between items-center md:items-end gap-4 border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6 min-w-[150px]">
                                    <div className="text-left md:text-right">
                                        <p className="text-3xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{job.candidateCount || 0}</p>
                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Ứng viên</p>
                                    </div>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/business/jobs/${job._id}/candidates`);
                                        }}
                                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm"
                                    >
                                        Xem ứng viên
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}